<template>
  <div class="settings-rfcs">
    <div class="settings-card-top"></div>

    <div class="settings-card-body">
      <br />
      <h4>Mis RFCs</h4>
      <p class="settings-subtitle">A continuación se muestran los RFCs que han sido configurados en tu cuenta.</p>
      <div v-if="sectionError" class="alert alert-danger" role="alert">{{ sectionError }}</div>

      <div class="rfcs-info-banner" v-if="rfcs.length === 0">
        <i class="fa fa-info-circle"></i>
        <div>
          <h6>Comienza registrando tu RFC</h6>
          <p>Dar de alta al menos un RFC te permite habilitar funciones clave para consultar y trabajar tus CFDIs dentro de la plataforma.</p>
        </div>
      </div>

      <!-- LISTADO DE RFC -->
      <div class="rfcs-history-card">
        <div class="table-responsive" v-if="rfcs.length > 0">
          <table class="table table-sm rfcs-history-table">
            <thead>
              <tr>
                <th>RFC / Razón Social</th>
                <th>Certificado (eFirma)</th>
                <th>Vigencia</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in rfcs" :key="`${item.rfc}-${index}`">
                <td class="rfc-identity-cell">
                  <div class="rfc-identity-rfc">{{ item.rfc || '-' }}</div>
                  <div class="rfc-identity-name">{{ item.name || '-' }}</div>
                </td>
                <td>{{ item.serial_number || '-' }}</td>
                <td>{{ item.deadline || '-' }}</td>
                <td>
                  <span class="badge" :class="item.available ? 'badge-light-success' : 'badge-light-danger'">
                    {{ item.available ? 'Activo' : 'Vencido' }}
                  </span>
                </td>
                <td class="text-end">
                  <button type="button" class="btn btn-sm btn-light-warning rfc-regimen-btn me-2" @click="openRegimensModal(item)" title="Configurar regímenes fiscales">
                    <i class="fa fa-tags"></i>
                  </button>
                  <button type="button" class="btn btn-sm btn-light-primary rfc-update-btn me-2" @click="startEfirmaUpdate(item)" title="Actualizar eFirma">
                    <i class="fa fa-refresh"></i>
                  </button>
                  <button type="button" class="btn btn-sm btn-light-danger rfc-delete-btn" @click="openDeleteModal(item)" title="Eliminar RFC">
                    <i class="fa fa-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- MODAL ELIMINAR RFC -->
      <div v-if="deleteModal.show" class="rfc-delete-overlay" @click.self="closeDeleteModal">
        <div class="rfc-delete-modal">
          <div class="rfc-delete-modal-header">
            <h5><i class="fa fa-exclamation-triangle text-danger me-2"></i>Eliminar RFC</h5>
            <button type="button" class="btn-close" @click="closeDeleteModal"></button>
          </div>

          <div class="rfc-delete-modal-body">
            <div v-if="deleteModal.error" class="alert alert-danger" role="alert">{{ deleteModal.error }}</div>
            <div class="rfc-delete-info-row">
              <div class="rfc-delete-info-item">
                <span class="rfc-delete-info-label">RFC</span>
                <span class="rfc-delete-info-value">{{ deleteModal.rfc.rfc }}</span>
              </div>
              <div class="rfc-delete-info-item">
                <span class="rfc-delete-info-label">Nombre o Razón Social</span>
                <span class="rfc-delete-info-value">{{ deleteModal.rfc.name || '-' }}</span>
              </div>
              <div class="rfc-delete-info-item">
                <span class="rfc-delete-info-label">Certificado (eFirma)</span>
                <span class="rfc-delete-info-value">{{ deleteModal.rfc.serial_number || '-' }}</span>
              </div>
              <div class="rfc-delete-info-item">
                <span class="rfc-delete-info-label">Vigencia</span>
                <span class="rfc-delete-info-value">{{ deleteModal.rfc.deadline || '-' }}</span>
              </div>
            </div>

            <div class="rfc-delete-counts" v-if="!deleteModal.loadingCounts">
              <div class="rfc-delete-count-card rfc-delete-count-issued">
                <i class="fa fa-file-text-o"></i>
                <span class="rfc-delete-count-num">{{ deleteModal.counts.issued }}</span>
                <span class="rfc-delete-count-label">CFDIs emitidos</span>
              </div>
              <div class="rfc-delete-count-card rfc-delete-count-received">
                <i class="fa fa-inbox"></i>
                <span class="rfc-delete-count-num">{{ deleteModal.counts.received }}</span>
                <span class="rfc-delete-count-label">CFDIs recibidos</span>
              </div>
            </div>
            <div class="rfc-delete-counts-loading" v-else>
              <i class="fa fa-spinner fa-spin"></i> Cargando información...
            </div>

            <div class="rfc-delete-warning">
              <i class="fa fa-warning"></i>
              <div>
                <strong>¿Estás seguro de que deseas eliminar este RFC?</strong>
                <p>Esta acción es <strong>irreversible</strong>. Se eliminarán permanentemente todos sus CFDIs, conceptos, impuestos, pagos, nóminas y toda la información relacionada.</p>
              </div>
            </div>
          </div>

          <div class="rfc-delete-modal-footer">
            <button type="button" class="btn btn-light" @click="closeDeleteModal" :disabled="deleteModal.deleting">Cancelar</button>
            <button type="button" class="btn btn-danger" @click="confirmDeleteRfc" :disabled="deleteModal.deleting || deleteModal.loadingCounts">
              <span v-if="deleteModal.deleting"><i class="fa fa-spinner fa-spin me-1"></i>Eliminando...</span>
              <span v-else><i class="fa fa-trash me-1"></i>Sí, eliminar RFC</span>
            </button>
          </div>
        </div>
      </div>

      <!-- MODAL CONFIGURAR REGIMENES RFC -->
      <div v-if="regimensModal.show" class="rfc-delete-overlay" @click.self="closeRegimensModal">
        <div class="rfc-delete-modal rfc-regimens-modal">
          <div class="rfc-delete-modal-header">
            <h5><i class="fa fa-tags text-warning me-2"></i>Configurar Regímenes Fiscales</h5>
            <button type="button" class="btn-close" @click="closeRegimensModal"></button>
          </div>

          <div class="rfc-delete-modal-body">
            <div v-if="regimensModal.error" class="alert alert-danger" role="alert">{{ regimensModal.error }}</div>

            <div class="rfc-delete-info-row mb-3">
              <div class="rfc-delete-info-item">
                <span class="rfc-delete-info-label">RFC</span>
                <span class="rfc-delete-info-value">{{ regimensModal.rfc.rfc || '-' }}</span>
              </div>
              <div class="rfc-delete-info-item">
                <span class="rfc-delete-info-label">Nombre o Razón Social</span>
                <span class="rfc-delete-info-value">{{ regimensModal.rfc.name || '-' }}</span>
              </div>
            </div>

            <div class="rfc-regimens-list" v-if="regimensModal.options.length">
              <label
                v-for="(regimen, index) in regimensModal.options"
                :key="`regimen-option-${index}`"
                class="rfc-regimen-option"
              >
                <span class="cc-switch">
                  <input
                    type="checkbox"
                    :checked="isRegimenSelected(regimen.code)"
                    @change="toggleRegimenSelection(regimen.code, $event)"
                  />
                  <span class="cc-slider"></span>
                </span>
                <span class="rfc-regimen-option-label">{{ regimen.label || `${regimen.code} - ${regimen.name}` }}</span>
              </label>
            </div>
            <div v-else class="text-muted">No hay regímenes fiscales disponibles.</div>
          </div>

          <div class="rfc-delete-modal-footer">
            <button type="button" class="btn btn-light" @click="closeRegimensModal" :disabled="regimensModal.saving">Cancelar</button>
            <button type="button" class="btn btn-warning" @click="saveRegimensConfig" :disabled="regimensModal.saving">
              <span v-if="regimensModal.saving"><i class="fa fa-spinner fa-spin me-1"></i>Guardando...</span>
              <span v-else><i class="fa fa-save me-1"></i>Guardar</span>
            </button>
          </div>
        </div>
      </div>

      <div v-if="hasReachedRfcLimit" class="rfc-limit-warning mt-3" role="alert">
        <div class="rfc-limit-warning__content">
          <i class="fa fa-clock-o"></i>
          <div>
            <h6>Alcanzaste el límite de RFCs para tu paquete ({{ rfcCount }}/{{ rfcLimit }})</h6>
            <p>{{ rfcLimitWarningMessage }}</p>
          </div>
        </div>
        <button type="button" class="btn btn-warning btn-sm" @click="goToSubscriptionSettings">
          Actualiza tu suscripción
        </button>
      </div>

      <!-- GUIA RAPIDA -->
      <div class="rfcs-history-card mt-3">
        <div class="p-3 p-md-4">
          <div class="row g-3 text-center rfcs-guide-row">
            <div class="col-12 col-md-4">
              <div class="d-flex flex-column align-items-center h-100">
                <span class="rfcs-step-circle">1</span>
                <p class="mb-0 mt-2 text-muted">Sube tu archivo <strong>.cer</strong> y <strong>.key</strong>.</p>
              </div>
            </div>

            <div class="col-12 col-md-4">
              <div class="d-flex flex-column align-items-center h-100">
                <span class="rfcs-step-circle">2</span>
                <p class="mb-0 mt-2 text-muted">El sistema leerá automáticamente RFC, Razón Social y vigencia desde el certificado.</p>
              </div>
            </div>

            <div class="col-12 col-md-4">
              <div class="d-flex flex-column align-items-center h-100">
                <span class="rfcs-step-circle">3</span>
                <p class="mb-0 mt-2 text-muted">Captura únicamente la contraseña de la Fiel para finalizar.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- FIEL -->
      <div class="esign-main-card">
        <div ref="esignUploadPanel" class="esign-upload-panel">
          <div class="esign-panel-header">
            <h5>Fiel</h5>
          </div>

          <form ref="fielForm" id="fiel_form" method="POST" enctype="multipart/form-data" autocomplete="off">
            <input type="text" style="display:none" autocomplete="username" />
            <input type="password" style="display:none" autocomplete="current-password" />

            <fieldset class="rfcs-form-fieldset" :disabled="isFormBlockedByRfcLimit">
            <div v-if="formSubmitError" class="alert alert-danger mb-3" role="alert">{{ formSubmitError }}</div>
            <div v-if="updateTargetRfc" class="alert alert-info mb-3" role="alert">
              <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
                <span>Actualizando eFirma para RFC <strong>{{ updateTargetRfc }}</strong>. El certificado (.cer) debe corresponder a este RFC.</span>
                <button type="button" class="btn btn-sm btn-light" @click="cancelEfirmaUpdate">Cancelar actualización</button>
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-12 col-lg-6">
                <div class="rfcs-upload-box" :class="{ 'rfcs-upload-box-invalid': !!formErrors.cer_file }">
                  <div class="rfcs-upload-icon"><i class="fa fa-cloud-upload"></i></div>
                  <h6>Certificado (.cer)</h6>
                  <p>Sube tu archivo .CER</p>
                  <button type="button" class="btn btn-light" @click="triggerCerFileInput">Seleccionar archivo</button>
                  <small class="d-block mt-2 text-muted rfcs-file-name" :title="cerFileName || 'Ningún archivo seleccionado'">{{ cerFileName || 'Ningún archivo seleccionado' }}</small>
                  <input
                    ref="cerFileInput"
                    class="d-none"
                    type="file"
                    id="cer_file"
                    name="cer_file"
                    accept=".cer"
                    autocomplete="off"
                    @change="onCerFileChange"
                  />
                  <small v-if="formErrors.cer_file" class="text-danger d-block mt-2">{{ formErrors.cer_file }}</small>
                </div>
              </div>

              <div class="col-12 col-lg-6">
                <div class="rfcs-upload-box" :class="{ 'rfcs-upload-box-invalid': !!formErrors.key_file }">
                  <div class="rfcs-upload-icon"><i class="fa fa-cloud-upload"></i></div>
                  <h6>Clave privada (.key)</h6>
                  <p>Sube tu archivo .KEY</p>
                  <button type="button" class="btn btn-light" @click="triggerKeyFileInput">Seleccionar archivo</button>
                  <small class="d-block mt-2 text-muted rfcs-file-name" :title="keyFileName || 'Ningún archivo seleccionado'">{{ keyFileName || 'Ningún archivo seleccionado' }}</small>
                  <input
                    ref="keyFileInput"
                    class="d-none"
                    type="file"
                    id="key_file"
                    name="key_file"
                    accept=".key"
                    autocomplete="off"
                    @change="onKeyFileChange"
                  />
                  <small v-if="formErrors.key_file" class="text-danger d-block mt-2">{{ formErrors.key_file }}</small>
                </div>
              </div>
            </div>

            <div class="mb-3">
              <label>Contraseña:</label>
              <input class="form-control" :class="{ 'is-invalid': !!formErrors.fiel_password }" v-model="password" type="password" id="fiel_password" name="fiel_password" autocomplete="off" @input="formErrors.fiel_password = ''" />
              <div v-if="formErrors.fiel_password" class="invalid-feedback d-block">{{ formErrors.fiel_password }}</div>
            </div>

            <div class="settings-grid esign-input-grid">
              <div class="settings-field">
                <label>RFC:</label>
                <input class="form-control" v-model="rfc" type="text" id="rfc" name="rfc" readonly style="background-color: #e9ecef;" />
              </div>

              <div class="settings-field">
                <label>Nombre o Razón Social:</label>
                <input class="form-control" v-model="name" type="text" id="name" name="name" readonly style="background-color: #e9ecef;" />
              </div>

              <div class="settings-field">
                <label>Inicio de Vigencia:</label>
                <input class="form-control" v-model="start_date" type="text" id="start_date" name="start_date" readonly style="background-color: #e9ecef;" />
              </div>

              <div class="settings-field">
                <label>Fin de Vigencia:</label>
                <input class="form-control" v-model="end_date" type="text" id="end_date" name="end_date" readonly style="background-color: #e9ecef;" />
              </div>
            </div>

            <div class="settings-actions esign-actions">
              <button type="button" class="btn btn-primary" :disabled="isFormBlockedByRfcLimit" @click="storeRFC">
                <i class="fa fa-lock"></i>
                {{ isReplacingExistingRfc ? 'Actualizar eFirma' : 'Guardar RFC' }}
              </button>
            </div>

            </fieldset>
          </form>
        </div>

        <div class="esign-faq-panel">
          <h6 class="esign-faq-title">
            <i class="fa fa-question-circle"></i>
            Preguntas frecuentes
          </h6>

          <div class="esign-faq-stack">
            <div class="esign-faq-card">
              <h6>¿QUÉ ES LA e.firma (FIEL)?</h6>
              <p>La e.firma (antes FIEL) es tu identidad digital ante el SAT. Incluye tu certificado (.cer), tu llave privada (.key) y tu contraseña, y sirve para autenticar que eres titular del RFC.</p>
            </div>

            <div class="esign-faq-card">
              <h6>¿PARA QUE NECESITAMOS LA e.firma?</h6>
              <p>La usamos para validar tu identidad fiscal y habilitar procesos automáticos como la consulta y sincronización de CFDIs asociados a tu RFC, sin depender de cargas manuales.</p>
            </div>

            <div class="esign-faq-card">
              <h6>¿QUÉ PASA SI MI e.firma ESTÁ VENCIDA?</h6>
              <p>Si tu e.firma está vencida, la conexión con SAT puede fallar y la descarga de CFDIs se detiene. Cuando la renueves, solo actualiza tus archivos .cer/.key y contraseña en esta sección para reactivar la sincronización.</p>
            </div>

            <div class="esign-faq-card">
              <h6>¿CÓMO PROTEGEMOS TU INFORMACIÓN?</h6>
              <p>Tus datos se manejan de forma confidencial, con controles de acceso y almacenamiento seguro. Solo se usan para las operaciones autorizadas por tu cuenta y nunca para fines distintos a la gestión fiscal dentro de la plataforma.</p>
            </div>
          </div>
        </div>
      </div>

      <InitialDownloadsModal
        :visible="initialDownloadsModal.visible"
        :rfc="initialDownloadsModal.rfc"
        @close="closeInitialDownloadsModal"
      />
    </div>
  </div>
</template>

<script>
import ws from '@/services/rfcs';
import accountService from '@/services/account';
import onboardingService from '@/services/onboarding';
import { pushGtmEvent } from '@/services/gtm';
import InitialDownloadsModal from '@/components/global/InitialDownloadsModal.vue';
import forge from 'node-forge';

const MORAL_REGIMEN_CODES = new Set(['601', '603', '610', '620', '623', '624', '626', '628', '629']);
const FISICA_REGIMEN_CODES = new Set(['605', '606', '607', '608', '611', '612', '614', '615', '616', '621', '622', '625', '626', '630']);

export default {
  name: 'SettingsRfcsSection',
  components: {
    InitialDownloadsModal,
  },
  data() {
    return {
      rfcs: [],
      rfc: '',
      name: '',
      password: '',
      start_date: '',
      end_date: '',
      cerFileName: '',
      keyFileName: '',
      formErrors: {
        cer_file: '',
        key_file: '',
        fiel_password: '',
      },
      deleteModal: {
        show: false,
        rfc: {},
        counts: { issued: 0, received: 0 },
        loadingCounts: false,
        deleting: false,
        error: '',
      },
      regimensModal: {
        show: false,
        saving: false,
        rfc: {},
        allOptions: [],
        options: [],
        selectedCodes: [],
        error: '',
      },
      subscriptionInfo: null,
      sectionError: '',
      formSubmitError: '',
      updateTargetRfc: '',
      initialDownloadsModal: {
        visible: false,
        rfc: '',
      },
    };
  },
  mounted: async function() {
    await this.loadSubscriptionInfo();
    await this.loadRfcs();
    this.focusEfirmaPanelIfRequested();
  },
  computed: {
    rfcLimit() {
      const limit = this.subscriptionInfo?.current_package?.rfc_limit;

      if (limit === null || limit === undefined) {
        return null;
      }

      return Number(limit);
    },
    rfcCount() {
      const count = this.subscriptionInfo?.rfcs_count;

      if (count === null || count === undefined) {
        return this.rfcs.length;
      }

      return Number(count);
    },
    hasReachedRfcLimit() {
      if (this.rfcLimit === null) {
        return false;
      }

      return this.rfcCount >= this.rfcLimit;
    },
    isReplacingExistingRfc() {
      const currentRfc = String(this.rfc || '').trim().toUpperCase();
      if (!currentRfc) {
        return false;
      }

      return this.rfcs.some((item) => String(item?.rfc || '').trim().toUpperCase() === currentRfc);
    },
    isFormBlockedByRfcLimit() {
      return this.hasReachedRfcLimit && !this.isReplacingExistingRfc;
    },
    rfcLimitWarningMessage() {
      return 'Si necesitas registrar más RFCs, actualiza tu suscripción para ampliar el límite de tu paquete.';
    },
  },
  methods: {
    goToSubscriptionSettings() {
      this.$router.push({
        name: 'settings',
        query: {
          section: 'subscription',
        },
      });
    },
    async loadSubscriptionInfo() {
      try {
        const response = await accountService.getSubscription();
        this.subscriptionInfo = response?.type === 'success' ? (response?.data || null) : null;
      } catch (error) {
        this.subscriptionInfo = null;
      }
    },
    async loadRfcs() {
      this.sectionError = '';
      try {
        const response = await ws.getRFCs();
        if (response && response.type === 'success') {
          this.rfcs = this.normalizeRfcs(Array.isArray(response.data) ? response.data : []);

          if (this.updateTargetRfc) {
            const stillExists = this.rfcs.some((item) => String(item?.rfc || '').trim().toUpperCase() === this.updateTargetRfc);
            if (!stillExists) {
              this.cancelEfirmaUpdate();
            }
          }

          this.syncMainRfcLocalStorage(this.rfcs);
          return;
        }

        this.rfcs = [];
        this.syncMainRfcLocalStorage([]);
      } catch (error) {
        this.rfcs = [];
        this.syncMainRfcLocalStorage([]);
        this.sectionError = 'No se pudieron consultar los RFCs.';
      }
    },
    async storeRFC() {
      this.formSubmitError = '';
      const isUpdatingExistingRfc = this.isReplacingExistingRfc;
      const registeredRfc = String(this.rfc || '').trim().toUpperCase();

      if (this.isFormBlockedByRfcLimit) {
        return;
      }

      if (!this.validateStoreRFCForm()) {
        this.formSubmitError = 'Completa los campos obligatorios antes de continuar.';
        return;
      }

      const form = this.$refs.fielForm;
      const formData = new FormData(form);

      try {
        const response = await ws.storeRFC(formData);

        if (response && response.type === 'success') {
          this.$toast.success(response.message);

          this.trackEfirmaUploadedEvent();

          this.syncMainRfcLocalStorage(Array.isArray(response.data) ? response.data : []);

          this.clearForm();
          await this.loadRfcs();
          await this.loadSubscriptionInfo();
          this.$emit('refresh', response.data);

          if (!isUpdatingExistingRfc) {
            this.openInitialDownloadsModal(registeredRfc);
          }

          return;
        }

        this.formSubmitError = response?.message || 'No se pudo registrar el RFC.';
      } catch (error) {
        this.formSubmitError = error?.response?.data?.message || 'No se pudo registrar el RFC.';
      }
    },
    openInitialDownloadsModal(rfc) {
      this.initialDownloadsModal.rfc = String(rfc || localStorage.getItem('rfc') || '').trim().toUpperCase();
      this.initialDownloadsModal.visible = true;
    },
    closeInitialDownloadsModal() {
      this.initialDownloadsModal.visible = false;
      this.initialDownloadsModal.rfc = '';
    },
    async trackEfirmaUploadedEvent() {
      pushGtmEvent('efirma_uploaded', {
        source: 'settings_rfcs',
      });

      try {
        await onboardingService.trackEvent({
          event_name: 'efirma_uploaded',
          step_key: 'upload_efirma',
        });
      } catch (error) {
      }
    },
    triggerCerFileInput() {
      if (this.isFormBlockedByRfcLimit) return;
      this.$refs.cerFileInput.click();
    },
    triggerKeyFileInput() {
      if (this.isFormBlockedByRfcLimit) return;
      this.$refs.keyFileInput.click();
    },
    async onCerFileChange(e) {
      if (this.isFormBlockedByRfcLimit) return;

      const file = e?.target?.files?.[0];
      this.cerFileName = file ? file.name : '';
      this.formErrors.cer_file = '';
      await this.loadCertificate(e);
    },
    onKeyFileChange(e) {
      if (this.isFormBlockedByRfcLimit) return;

      const file = e?.target?.files?.[0];
      this.keyFileName = file ? file.name : '';
      this.formErrors.key_file = '';
    },
    validateStoreRFCForm() {
      this.formErrors.cer_file = '';
      this.formErrors.key_file = '';
      this.formErrors.fiel_password = '';

      const cerFile = this.$refs?.cerFileInput?.files?.[0];
      const keyFile = this.$refs?.keyFileInput?.files?.[0];
      const password = String(this.password || '').trim();

      if (!cerFile) {
        this.formErrors.cer_file = 'Debes seleccionar un archivo .cer';
      }

      if (!keyFile) {
        this.formErrors.key_file = 'Debes seleccionar un archivo .key';
      }

      if (!password) {
        this.formErrors.fiel_password = 'Debes capturar la contrasena de la FIEL';
      }

      return !this.formErrors.cer_file && !this.formErrors.key_file && !this.formErrors.fiel_password;
    },
    async loadCertificate(e) {
      const file = e?.target?.files?.[0];

      if (!file) {
        return;
      }

      try {
        const arrayBuffer = await file.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        let binary = '';

        for (let i = 0; i < uint8Array.byteLength; i++) {
          binary += String.fromCharCode(uint8Array[i]);
        }

        const der = forge.util.createBuffer(binary, 'raw');
        const asn1 = forge.asn1.fromDer(der);
        const cert = forge.pki.certificateFromAsn1(asn1);

        const subjectAttrs = cert.subject.attributes;
        const rfcAttr = subjectAttrs.find((attr) => attr.type === '2.5.4.45');
        const nameAttr = subjectAttrs.find((attr) => attr.type === '2.5.4.3');

        const rfcAttrParts = (rfcAttr?.value || '')
          .split('/')
          .map((part) => part.trim())
          .filter(Boolean);

        const parsedRfc = String(rfcAttrParts[0] || '').toUpperCase();

        if (this.updateTargetRfc && parsedRfc !== this.updateTargetRfc) {
          this.formErrors.cer_file = 'La eFirma no le pertenece al RFC seleccionado para actualización.';
          this.cerFileName = '';
          if (this.$refs?.cerFileInput) {
            this.$refs.cerFileInput.value = '';
          }
          this.start_date = '';
          this.end_date = '';
          return;
        }

        this.rfc = parsedRfc;
        this.name = nameAttr ? nameAttr.value : (rfcAttrParts[1] || '');

        const formatDate = (date) => date.toLocaleDateString('es-MX', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        });

        this.start_date = formatDate(cert.validity.notBefore);
        this.end_date = formatDate(cert.validity.notAfter);
      } catch (error) {
        this.formErrors.cer_file = 'No se pudo leer el archivo .cer.';
      }
    },
    startEfirmaUpdate(item) {
      const selectedRfc = String(item?.rfc || '').trim().toUpperCase();
      if (!selectedRfc) {
        return;
      }

      this.updateTargetRfc = selectedRfc;
      this.rfc = selectedRfc;
      this.name = String(item?.name || '');
      this.password = '';
      this.start_date = '';
      this.end_date = '';
      this.cerFileName = '';
      this.keyFileName = '';
      this.formErrors.cer_file = '';
      this.formErrors.key_file = '';
      this.formErrors.fiel_password = '';
      this.formSubmitError = '';

      if (this.$refs?.fielForm) {
        this.$refs.fielForm.reset();
      }

      if (this.$refs?.esignUploadPanel && typeof this.$refs.esignUploadPanel.scrollIntoView === 'function') {
        this.$refs.esignUploadPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    cancelEfirmaUpdate() {
      this.clearForm();
    },
    async openDeleteModal(item) {
      this.deleteModal.rfc = item;
      this.deleteModal.counts = { issued: 0, received: 0 };
      this.deleteModal.loadingCounts = true;
      this.deleteModal.deleting = false;
      this.deleteModal.error = '';
      this.deleteModal.show = true;

      try {
        const response = await ws.getCfdiCountsByRfc(item.rfc);
        if (response && response.type === 'success') {
          this.deleteModal.counts = response.data;
        }
      } catch (e) {
        // counts remain at 0, no blocking error
      } finally {
        this.deleteModal.loadingCounts = false;
      }
    },
    closeDeleteModal() {
      if (this.deleteModal.deleting) return;
      this.deleteModal.show = false;
      this.deleteModal.error = '';
    },
    async confirmDeleteRfc() {
      this.deleteModal.deleting = true;
      this.deleteModal.error = '';

      try {
        const response = await ws.deleteRFC(this.deleteModal.rfc.rfc);

        if (response && response.type === 'success') {
          this.$toast.success(response.message || 'El RFC ha sido eliminado correctamente.');
          this.deleteModal.show = false;
          this.rfcs = this.normalizeRfcs(Array.isArray(response.data) ? response.data : []);
          await this.loadSubscriptionInfo();
          this.syncMainRfcLocalStorage(this.rfcs);
          this.$emit('refresh', response.data);
          return;
        }

        this.deleteModal.error = response?.message || 'No se pudo eliminar el RFC.';
      } catch (e) {
        this.deleteModal.error = e?.response?.data?.message || 'No se pudo eliminar el RFC.';
      } finally {
        this.deleteModal.deleting = false;
      }
    },
    async openRegimensModal(item) {
      this.regimensModal.show = true;
      this.regimensModal.error = '';
      this.regimensModal.saving = false;
      this.regimensModal.rfc = item || {};
      this.regimensModal.selectedCodes = Array.isArray(item?.regimen_codes)
        ? item.regimen_codes.map((code) => String(code || '').trim().toUpperCase()).filter(Boolean)
        : [];

      if (this.regimensModal.allOptions.length === 0) {
        try {
          const response = await ws.getRegimensCatalog();
          if (response && response.type === 'success') {
            this.regimensModal.allOptions = Array.isArray(response.data) ? response.data : [];
          } else {
            this.regimensModal.error = response?.message || 'No se pudo consultar el catálogo de regímenes fiscales.';
            return;
          }
        } catch (error) {
          this.regimensModal.error = error?.response?.data?.message || 'No se pudo consultar el catálogo de regímenes fiscales.';
          return;
        }
      }

      this.regimensModal.options = this.filterRegimensByRfcType(this.regimensModal.allOptions, this.regimensModal.rfc?.rfc);

      const allowedCodes = new Set(
        this.regimensModal.options
          .map((regimen) => String(regimen?.code || '').trim().toUpperCase())
          .filter(Boolean),
      );

      this.regimensModal.selectedCodes = this.regimensModal.selectedCodes.filter((code) => allowedCodes.has(code));
    },
    detectRfcPersonType(rfc) {
      const normalized = String(rfc || '').trim().toUpperCase();
      if (normalized.length === 12) {
        return 'moral';
      }

      if (normalized.length === 13) {
        return 'fisica';
      }

      return '';
    },
    filterRegimensByRfcType(options, rfc) {
      const personType = this.detectRfcPersonType(rfc);
      const list = Array.isArray(options) ? options : [];

      if (!personType) {
        return list;
      }

      return list.filter((regimen) => {
        const code = String(regimen?.code || '').trim().toUpperCase();

        if (!code) {
          return false;
        }

        if (personType === 'moral') {
          return MORAL_REGIMEN_CODES.has(code);
        }

        if (personType === 'fisica') {
          return FISICA_REGIMEN_CODES.has(code);
        }

        return true;
      });
    },
    closeRegimensModal() {
      if (this.regimensModal.saving) return;
      this.regimensModal.show = false;
      this.regimensModal.error = '';
    },
    isRegimenSelected(code) {
      const normalized = String(code || '').trim().toUpperCase();
      return this.regimensModal.selectedCodes.includes(normalized);
    },
    toggleRegimenSelection(code, event) {
      const normalized = String(code || '').trim().toUpperCase();
      if (!normalized) {
        return;
      }

      if (event?.target?.checked) {
        if (!this.regimensModal.selectedCodes.includes(normalized)) {
          this.regimensModal.selectedCodes.push(normalized);
        }
        return;
      }

      this.regimensModal.selectedCodes = this.regimensModal.selectedCodes.filter((item) => item !== normalized);
    },
    async saveRegimensConfig() {
      this.regimensModal.error = '';
      this.regimensModal.saving = true;

      try {
        const response = await ws.setRfcRegimens(this.regimensModal.rfc.rfc, this.regimensModal.selectedCodes);

        if (response && response.type === 'success') {
          this.$toast.success(response.message || 'Los regímenes fiscales se actualizaron correctamente.');
          this.rfcs = this.normalizeRfcs(Array.isArray(response.data) ? response.data : []);
          this.regimensModal.show = false;
          this.$emit('refresh', response.data);
          return;
        }

        this.regimensModal.error = response?.message || 'No se pudo guardar la configuración de regímenes fiscales.';
      } catch (error) {
        this.regimensModal.error = error?.response?.data?.message || 'No se pudo guardar la configuración de regímenes fiscales.';
      } finally {
        this.regimensModal.saving = false;
      }
    },
    normalizeRfcs(list) {
      return (Array.isArray(list) ? list : []).map((item) => {
        const regimens = Array.isArray(item?.regimens) ? item.regimens : [];
        const regimenCodes = Array.isArray(item?.regimen_codes)
          ? item.regimen_codes
          : regimens.map((regimen) => regimen?.code);

        return {
          ...item,
          regimens,
          regimen_codes: regimenCodes
            .map((code) => String(code || '').trim().toUpperCase())
            .filter(Boolean),
        };
      });
    },
    syncMainRfcLocalStorage(rfcList = []) {
      const demoRfc = 'XAXX010101000';
      const normalized = (Array.isArray(rfcList) ? rfcList : [])
        .map((item) => {
          if (typeof item === 'string') {
            return {
              rfc: String(item || '').trim(),
              name: '',
            };
          }

          if (item && typeof item === 'object') {
            return {
              rfc: String(item.rfc || item.RFC || '').trim(),
              name: String(item.name || item.fiscal_name || item.razon_social || '').trim(),
            };
          }

          return { rfc: '', name: '' };
        })
        .filter((item) => item.rfc);

      if (!normalized.length) {
        const currentRfc = String(localStorage.getItem('rfc') || '').trim().toUpperCase();
        const rawStoredRfcs = localStorage.getItem('rfcs');

        let stored = [];
        if (rawStoredRfcs && rawStoredRfcs !== 'undefined') {
          try {
            const parsed = JSON.parse(rawStoredRfcs);
            stored = Array.isArray(parsed) ? parsed : [];
          } catch (_) {
            stored = [];
          }
        }

        const hasStoredDemo = stored.some((item) => {
          const value = typeof item === 'string'
            ? item
            : (item?.rfc || item?.RFC || '');
          return String(value || '').trim().toUpperCase() === demoRfc;
        });

        if (currentRfc === demoRfc || hasStoredDemo) {
          window.dispatchEvent(new CustomEvent('rfcs-storage-updated'));
          return;
        }
      }

      localStorage.setItem('rfcs', JSON.stringify(normalized));

      const currentMain = String(localStorage.getItem('rfc') || '').trim();
      const currentMatch = normalized.find((item) => item.rfc === currentMain);

      if (currentMatch) {
        localStorage.setItem('fiscal_name', currentMatch.name || '');
        window.dispatchEvent(new CustomEvent('rfcs-storage-updated'));
        return;
      }

      if (normalized.length > 0) {
        localStorage.setItem('rfc', normalized[0].rfc);
        localStorage.setItem('fiscal_name', normalized[0].name || '');
        window.dispatchEvent(new CustomEvent('rfcs-storage-updated'));
        return;
      }

      localStorage.setItem('rfc', '');
      localStorage.setItem('fiscal_name', '');
      window.dispatchEvent(new CustomEvent('rfcs-storage-updated'));
    },
    clearForm() {
      this.updateTargetRfc = '';
      this.rfc = '';
      this.name = '';
      this.password = '';
      this.start_date = '';
      this.end_date = '';
      this.cerFileName = '';
      this.keyFileName = '';
      this.formErrors.cer_file = '';
      this.formErrors.key_file = '';
      this.formErrors.fiel_password = '';
      this.formSubmitError = '';

      if (this.$refs.fielForm) {
        this.$refs.fielForm.reset();
      }
    },
    focusEfirmaPanelIfRequested() {
      const shouldFocus = String(this.$route?.query?.onboarding || '').trim() === 'efirma';

      if (!shouldFocus) {
        return;
      }

      this.$nextTick(() => {
        if (this.$refs?.esignUploadPanel && typeof this.$refs.esignUploadPanel.scrollIntoView === 'function') {
          this.$refs.esignUploadPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },
  },
};
</script>

<style scoped>
.rfcs-guide-row {
  position: relative;
}

.rfcs-step-circle {
  width: 44px;
  height: 44px;
  border: 4px solid transparent;
  border-radius: 50%;
  display: inline-grid;
  place-items: center;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  color: var(--theme-deafult, #7366ff);
  background:
    linear-gradient(var(--bs-white, #fff), var(--bs-white, #fff)) padding-box,
    linear-gradient(90deg, #ed6793, #5d62a9, #72c6ff) border-box;
  padding-bottom: 0px;
}

.rfcs-upload-box {
  border: 2px solid transparent;
  border-radius: 14px;
  padding: 20px 16px;
  text-align: center;
  background:
    linear-gradient(var(--bs-white, #fff), var(--bs-white, #fff)) padding-box,
    linear-gradient(90deg, #ed6793, #5d62a9, #72c6ff) border-box;
}

.rfcs-upload-icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 12px;
  border-radius: 50%;
  display: inline-grid;
  place-items: center;
  background-color: #f2f3f8;
  color: var(--theme-deafult, #7366ff);
  font-size: 20px;
}

.rfcs-upload-box h6 {
  margin-bottom: 6px;
}

.rfcs-upload-box p {
  margin-bottom: 12px;
  color: #6c757d;
}

.rfcs-upload-box-invalid {
  border-color: #dc3545;
}

.rfcs-file-name {
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rfcs-form-fieldset[disabled] {
  opacity: 0.6;
}

/* Delete button */
.rfc-delete-btn {
  padding: 4px 8px;
  font-size: 12px;
  line-height: 1.4;
  border: 1px solid #f1416c33;
  background-color: #fff5f8;
  color: #f1416c;
  border-radius: 6px;
  transition: background-color 0.15s, color 0.15s;
}

.rfc-update-btn {
  padding: 4px 8px;
  font-size: 12px;
  line-height: 1.4;
  border: 1px solid #3699ff33;
  background-color: #f1f8ff;
  color: #3699ff;
  border-radius: 6px;
  transition: background-color 0.15s, color 0.15s;
}

.rfc-regimen-btn {
  padding: 4px 8px;
  font-size: 12px;
  line-height: 1.4;
  border: 1px solid #ffc10755;
  background-color: #fff8e1;
  color: #b8860b;
  border-radius: 6px;
  transition: background-color 0.15s, color 0.15s;
}

.rfc-regimen-btn:hover {
  background-color: #ffc107;
  color: #fff;
  border-color: #ffc107;
}

.rfc-regimens-cell {
  min-width: 280px;
}

.rfc-identity-cell {
  min-width: 240px;
}

.rfc-identity-rfc {
  font-weight: 700;
  color: #1f253d;
  line-height: 1.25;
}

.rfc-identity-name {
  margin-top: 2px;
  line-height: 1.3;
  color: #67738e;
}

.rfc-regimen-line {
  line-height: 1.35;
}

.rfc-regimen-inline {
  line-height: 1.35;
}

.rfc-regimens-list {
  max-height: 45vh;
  overflow-y: auto;
  border: 1px solid #eceff5;
  border-radius: 10px;
  padding: 8px 10px;
}

.rfc-regimen-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #f2f3f8;
  cursor: pointer;
}

.rfc-regimen-option:last-child {
  border-bottom: none;
}

.rfc-regimen-option-label {
  font-size: 13px;
  color: #3d4465;
  white-space: nowrap;
}

.rfc-delete-modal.rfc-regimens-modal {
  width: min(800px, 96vw);
  max-width: 800px;
}

.cc-switch {
  position: relative;
  display: inline-block;
  width: 42px;
  height: 22px;
}

.cc-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.cc-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background-color: #d1d5db;
  transition: 0.2s;
  border-radius: 999px;
}

.cc-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 2px;
  background-color: #ffffff;
  transition: 0.2s;
  border-radius: 999px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.25);
}

.cc-switch input:checked + .cc-slider {
  background-color: #f59e0b;
}

.cc-switch input:checked + .cc-slider:before {
  transform: translateX(18px);
}

.rfc-update-btn:hover {
  background-color: #3699ff;
  color: #fff;
  border-color: #3699ff;
}
.rfc-delete-btn:hover {
  background-color: #f1416c;
  color: #fff;
  border-color: #f1416c;
}

/* Modal overlay */
.rfc-delete-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1055;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.rfc-delete-modal {
  background: #fff;
  border-radius: 14px;
  width: 100%;
  max-width: 520px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
  overflow: hidden;
}

.rfc-delete-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px 14px;
  border-bottom: 1px solid #f2f3f8;
}
.rfc-delete-modal-header h5 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
}

.rfc-delete-modal-body {
  padding: 20px 24px;
}

.rfc-delete-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 24px 18px;
  border-top: 1px solid #f2f3f8;
}

/* RFC info row */
.rfc-delete-info-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 18px;
  padding: 14px 16px;
  background: #f8f9fc;
  border-radius: 10px;
}
.rfc-delete-info-item {
  display: flex;
  flex-direction: column;
  flex: 1 1 140px;
}
.rfc-delete-info-label {
  font-size: 11px;
  color: #9aa0ac;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 2px;
}
.rfc-delete-info-value {
  font-size: 13px;
  font-weight: 600;
  color: #3d4465;
}

/* CFDI counts */
.rfc-delete-counts {
  display: flex;
  gap: 12px;
  margin-bottom: 18px;
}
.rfc-delete-count-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 14px 12px;
  border-radius: 10px;
  font-size: 12px;
}
.rfc-delete-count-issued {
  background: #f0f0ff;
  color: #7366ff;
}
.rfc-delete-count-received {
  background: #f0fff8;
  color: #0cc;
}
.rfc-delete-count-card i {
  font-size: 20px;
  margin-bottom: 2px;
}
.rfc-delete-count-num {
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
}
.rfc-delete-count-label {
  color: inherit;
  opacity: 0.8;
}
.rfc-delete-counts-loading {
  text-align: center;
  color: #9aa0ac;
  padding: 16px 0;
  margin-bottom: 18px;
}

/* Warning banner */
.rfc-delete-warning {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  background: #fff8f0;
  border: 1px solid #ffc107;
  border-radius: 10px;
  padding: 14px 16px;
  color: #7a5800;
  font-size: 13px;
}
.rfc-delete-warning i {
  font-size: 18px;
  color: #ffc107;
  margin-top: 2px;
  flex-shrink: 0;
}
.rfc-delete-warning p {
  margin: 4px 0 0;
}

.rfc-limit-warning {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  border: 1px solid #ffe3a3;
  background: #fff9e8;
  border-radius: 10px;
  padding: 12px 14px;
}

.rfc-limit-warning__content {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: #7a5a00;
}

.rfc-limit-warning__content i {
  font-size: 18px;
  margin-top: 2px;
  color: #d49b00;
}

.rfc-limit-warning__content h6 {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: #7a5a00;
}

.rfc-limit-warning__content p {
  margin: 2px 0 0;
  font-size: 12px;
  color: #8a6a14;
}

@media (max-width: 767px) {
  .rfc-limit-warning {
    flex-direction: column;
    align-items: stretch;
  }

  .rfc-limit-warning .btn {
    width: 100%;
  }
}
</style>
